import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError, NotificationsResponse } from 'src/types/TApiQueries';
import { TNotification, TTaskAction } from 'src/types/TNotification';
import { deleteNotifications, resolveTask } from '../functions/notificationsApi';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { queryNames } from '../utils/queryNames';

export const useDeleteNotificationsMutation = (mutationOptions?: UseMutationOptions<any, FetchError, any>) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: number[]) => deleteNotifications(data, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, deletedNotifications, ...args) => {
      queryClient.setQueryData([queryNames.notification, 'all'], (currentData: any) => {
        const { notifications } = currentData;

        const newNotifications = notifications.filter(
          (notification: TNotification) => !deletedNotifications.includes(notification.id),
        );

        return {
          ...currentData,
          notifications: newNotifications,
        };
      });

      mutationOptions?.onSuccess?.(result, deletedNotifications, ...args);
    },
  });
};

type ResolveTaskArgs = { id: number; action: TTaskAction };

export const useResolveTaskMutation = (
  mutationOptions?: UseMutationOptions<any, FetchError, ResolveTaskArgs>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(({ id, action }: ResolveTaskArgs) => resolveTask(id, action, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, variables, ...args) => {
      // The requester's list ("reviews I assigned") holds the SAME task under
      // a different key, and shows it with its new status rather than
      // dropping it — a withdrawn or declined review still belongs on the
      // list that answers "what happened to what I asked for". So invalidate
      // that one instead of pruning it, or the row keeps its old status until
      // something else happens to refetch.
      queryClient.invalidateQueries([queryNames.notification, 'assigned']);

      // Resolved tasks drop off the recipient's list immediately.
      queryClient.setQueryData([queryNames.notification, 'all'], (currentData: any) => {
        if (!currentData?.notifications) return currentData;

        const newNotifications = currentData.notifications.filter(
          (notification: TNotification) => notification.id !== variables.id,
        );

        return {
          ...currentData,
          notifications: newNotifications,
        };
      });

      mutationOptions?.onSuccess?.(result, variables, ...args);
    },
  });
};
